import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Scale, FileText, AlertTriangle, Shield, Clock, Mail } from "lucide-react";
import {
  BRAND_OPERATOR_LINE,
  COMPANY,
  formatCompanyMailingAddress,
} from "@/lib/companyInfo";

const Terms = () => {
  useSEO({
    title: "Terms of Service - Female Partner Calculator | User Agreement",
    description:
      "Terms of service for Female Partner Calculator, operated by Summit Lane Digital, LLC. Acceptable use, disclaimers, and limitations of liability.",
    canonical: "https://femalepartnercalculator.com/terms",
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Scale className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Terms of Service</h1>
            </div>
            <p className="text-lg text-muted-foreground">
              Please read these terms before using {COMPANY.brandName}.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Last updated: July 2026</span>
            </div>
          </div>

          {/* Operator */}
          <Card>
            <CardHeader>
              <CardTitle>Operator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-muted-foreground">
              <p>{BRAND_OPERATOR_LINE}</p>
              <p>
                <strong>Legal entity:</strong> {COMPANY.legalName}
              </p>
              <p>
                <strong>Mailing address:</strong> {formatCompanyMailingAddress()}
              </p>
            </CardContent>
          </Card>

          {/* Acceptance of Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                By accessing or using {COMPANY.brandName} (&quot;we,&quot; &quot;our,&quot; or &quot;the
                Service&quot;), you agree to be bound by these Terms of Service. If you do not agree, please
                do not use the Service.
              </p>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Important:</strong> These terms are a legally binding agreement between you and{" "}
                  {COMPANY.legalName} (operator of the {COMPANY.brandName} brand).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Service Description */}
          <Card>
            <CardHeader>
              <CardTitle>Service Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {COMPANY.brandName} is a free statistical tool that estimates the probability of finding a
                partner who meets a combination of demographic and behavioral criteria, based on publicly
                published data from the U.S. Census Bureau, CDC NHANES, and Pew Research Center. The Service
                is provided for informational and entertainment purposes only.
              </p>
              <p className="text-muted-foreground">
                All calculators, statistics pages, and data tools are free to use. There is no account
                creation, subscription, or paywall. An optional PayPal &quot;Donate&quot; button and an
                occasional sponsored/affiliate link may be shown to support the free Service.
              </p>
            </CardContent>
          </Card>

          {/* Not Advice / Non-Affiliation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Not Relationship Advice; Non-Affiliation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200 font-semibold mb-2">
                  Important Notice
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Results are population-level statistical probabilities, not predictions about any
                  individual relationship or dating outcome. They do not account for personal factors,
                  geographic location, social circles, or compatibility beyond the measured criteria, and
                  should not be treated as relationship, dating, or life advice.
                </p>
              </div>
              <p className="text-muted-foreground">
                {COMPANY.brandName} is an independent service and is not affiliated with, endorsed by, or
                sponsored by the U.S. Government, the U.S. Census Bureau, the CDC, Pew Research Center, or
                any other government agency or research institution. References to their data are for
                attribution only.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate Disclosure */}
          <Card>
            <CardHeader>
              <CardTitle>Affiliate & Sponsored Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {COMPANY.brandName} may display a sponsored or affiliate link to third-party services (for
                example, a dating platform). If you click through and sign up or make a purchase, we may
                earn a commission at no additional cost to you. Sponsored placements are clearly labeled as
                a disclosure. We are not responsible for the content, policies, or practices of any
                third-party site reached through these links.
              </p>
            </CardContent>
          </Card>

          {/* Acceptable Use */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Acceptable Use
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground mb-2">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated tools to scrape or harvest data at disruptive volume</li>
                <li>Reproduce, resell, or create derivative products from the Service without permission</li>
              </ul>
            </CardContent>
          </Card>

          {/* Donations */}
          <Card>
            <CardHeader>
              <CardTitle>Donations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                The optional &quot;Donate&quot; button is a voluntary gift processed by PayPal to help
                support the free Service, not a purchase of any product or service. Donations are not
                refundable. We do not sell any product, subscription, or paid report through this Service.
              </p>
            </CardContent>
          </Card>

          {/* Disclaimer of Warranties */}
          <Card>
            <CardHeader>
              <CardTitle>Disclaimer of Warranties</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                The Service and all data, statistics, and results are provided &quot;as is&quot; and
                &quot;as available,&quot; without warranties of any kind, express or implied, including
                accuracy, completeness, merchantability, or fitness for a particular purpose. Underlying
                government and research data may contain errors, estimates, or margins of error, and may be
                updated or revised without notice.
              </p>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle>Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                To the fullest extent permitted by law, {COMPANY.legalName} and its {COMPANY.brandName}{" "}
                brand shall not be liable for any indirect, incidental, special, consequential, or punitive
                damages, or any loss of data, opportunity, or goodwill, arising from your use of, or
                inability to use, the Service, even if advised of the possibility of such damages. Your sole
                remedy for dissatisfaction with the Service is to stop using it.
              </p>
            </CardContent>
          </Card>

          {/* Intellectual Property */}
          <Card>
            <CardHeader>
              <CardTitle>Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                The {COMPANY.brandName} name, logo, calculator methodology, and original content are owned
                by {COMPANY.legalName}. Underlying government and research statistics remain the property of
                their respective sources and are cited accordingly. You may share links to the Service and
                quote brief excerpts with attribution, but may not reproduce the Service or its content
                wholesale without permission.
              </p>
            </CardContent>
          </Card>

          {/* Changes to Terms */}
          <Card>
            <CardHeader>
              <CardTitle>Changes to These Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We may update these Terms from time to time. Material changes will be reflected by an
                updated &quot;Last updated&quot; date on this page. Continued use of the Service after
                changes take effect constitutes acceptance of the revised Terms.
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card>
            <CardHeader>
              <CardTitle>Governing Law</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                These Terms are governed by the laws of the State of {COMPANY.formationState}, without
                regard to conflict-of-law principles, as {COMPANY.legalName} is a{" "}
                {COMPANY.formationState} limited liability company.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Questions about these Terms? Contact us:
              </p>
              <div className="space-y-2">
                <p>
                  <strong>Email:</strong>{" "}
                  <a
                    href={`mailto:${COMPANY.emails.legal}`}
                    className="text-primary hover:underline"
                  >
                    {COMPANY.emails.legal}
                  </a>
                </p>
                <p>
                  <strong>Address:</strong> {formatCompanyMailingAddress()}
                </p>
                <p className="text-sm">
                  See also our{" "}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
